type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function formatter(timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  });
}

function partsAt(value: number | Date, timeZone: string): ZonedParts {
  const fields = formatter(timeZone).formatToParts(value);
  const field = (type: Intl.DateTimeFormatPartTypes) => Number(fields.find((part) => part.type === type)?.value);
  return {
    year: field("year"),
    month: field("month"),
    day: field("day"),
    hour: field("hour"),
    minute: field("minute")
  };
}

function parseLocalDateTime(date: string, time: string): ZonedParts | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match || !timeMatch) return undefined;
  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2])
  };
  const check = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute));
  if (
    check.getUTCFullYear() !== parts.year ||
    check.getUTCMonth() + 1 !== parts.month ||
    check.getUTCDate() !== parts.day ||
    check.getUTCHours() !== parts.hour ||
    check.getUTCMinutes() !== parts.minute
  ) {
    return undefined;
  }
  return parts;
}

function sameParts(left: ZonedParts, right: ZonedParts): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day && left.hour === right.hour && left.minute === right.minute;
}

/**
 * Converts a date/time entered in an event's IANA timezone to an ISO instant.
 * Nonexistent local times (for example, during a spring DST jump) are rejected.
 */
export function zonedDateTimeToIso(date: string, time: string, timeZone: string): string | undefined {
  const desired = parseLocalDateTime(date, time);
  if (!desired) return undefined;

  const desiredAsUtc = Date.UTC(desired.year, desired.month - 1, desired.day, desired.hour, desired.minute);
  let instant = desiredAsUtc;
  try {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const observed = partsAt(instant, timeZone);
      const observedAsUtc = Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute);
      const next = desiredAsUtc - (observedAsUtc - instant);
      if (next === instant) break;
      instant = next;
    }
    return sameParts(partsAt(instant, timeZone), desired) ? new Date(instant).toISOString() : undefined;
  } catch {
    return undefined;
  }
}

export function isoToZonedDateTimeInput(value: string | null | undefined, timeZone: string): { date: string; time: string } {
  if (!value || Number.isNaN(Date.parse(value))) return { date: "", time: "" };
  const parts = partsAt(new Date(value), timeZone);
  return {
    date: String(parts.year).padStart(4, "0") + "-" + String(parts.month).padStart(2, "0") + "-" + String(parts.day).padStart(2, "0"),
    time: String(parts.hour).padStart(2, "0") + ":" + String(parts.minute).padStart(2, "0")
  };
}

export function formatZonedDateTime(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone
  }).format(new Date(value));
}
